import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { API_URL, API_BASE_URL } from "../config/api";
import {
  Users,
  Plus,
  Trash2,
  Edit,
  Building2,
  Search,
  X,
  ChevronRight,
  Loader2,
  UserPlus,
  Check,
} from "lucide-react";


export default function CustomerGroupsPage() {
  const { token } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(null);
  
  // Create/Edit dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupName, setGroupName] = useState("");
  const [groupDescription, setGroupDescription] = useState("");
  const [saving, setSaving] = useState(false);
  
  // Add member dialog
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [companySearch, setCompanySearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [addingMember, setAddingMember] = useState(false);

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${API_URL}/customer-groups`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGroups(response.data.groups || []);
    } catch (error) {
      toast.error("Failed to load customer groups");
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupDetails = async (groupId) => {
    try {
      const response = await axios.get(`${API_URL}/customer-groups/${groupId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedGroup(response.data);
    } catch (error) {
      toast.error("Failed to load group details");
    }
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim()) {
      toast.error("Group name is required");
      return;
    }
    
    setSaving(true);
    try {
      if (editingGroup) {
        await axios.put(
          `${API_URL}/customer-groups/${editingGroup.id}`,
          { name: groupName.trim(), description: groupDescription.trim() || null },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Group updated");
      } else {
        await axios.post(
          `${API_URL}/customer-groups`,
          { name: groupName.trim(), description: groupDescription.trim() || null },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Group created");
      }
      
      setShowCreateDialog(false);
      setGroupName("");
      setGroupDescription("");
      setEditingGroup(null);
      fetchGroups();
      if (selectedGroup) {
        fetchGroupDetails(selectedGroup.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save group");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!confirm("Delete this customer group? Members will not be deleted.")) return;
    
    try {
      await axios.delete(`${API_URL}/customer-groups/${groupId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Group deleted");
      if (selectedGroup?.id === groupId) {
        setSelectedGroup(null);
      }
      fetchGroups();
    } catch (error) {
      toast.error("Failed to delete group");
    }
  };

  const searchCompanies = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const response = await axios.get(
        `${API_URL}/staff/mobile/crm-search?q=${encodeURIComponent(query)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSearchResults(response.data.companies || []);
    } catch (error) {
      console.error("Search failed", error);
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (companySearch) searchCompanies(companySearch);
    }, 300);
    return () => clearTimeout(debounce);
  }, [companySearch]);

  const handleAddMember = async (company) => {
    if (!selectedGroup) return;
    
    setAddingMember(true);
    try {
      await axios.post(
        `${API_URL}/customer-groups/${selectedGroup.id}/members`,
        { company_id: company.id, company_name: company.name },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${company.name} added to group`);
      setShowAddMemberDialog(false);
      setCompanySearch("");
      setSearchResults([]);
      fetchGroupDetails(selectedGroup.id);
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add member");
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async (companyId, companyName) => {
    if (!selectedGroup) return;
    if (!confirm(`Remove ${companyName} from this group?`)) return;
    
    try {
      await axios.delete(
        `${API_URL}/customer-groups/${selectedGroup.id}/members/${companyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${companyName} removed from group`);
      fetchGroupDetails(selectedGroup.id);
      fetchGroups();
    } catch (error) {
      toast.error("Failed to remove member");
    }
  };

  const openEditDialog = (group) => {
    setEditingGroup(group);
    setGroupName(group.name);
    setGroupDescription(group.description || "");
    setShowCreateDialog(true);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 py-6 pt-20">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Customer Groups</h1>
            <p className="text-slate-500 mt-1">
              Group related companies for unified order views
            </p>
          </div>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-group-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Group
          </Button>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Groups List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border shadow-sm">
              <div className="p-4 border-b">
                <h2 className="font-semibold text-slate-800">Groups</h2>
              </div>
              
              {loading ? (
                <div className="p-8 text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
                </div>
              ) : groups.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No customer groups yet</p>
                  <p className="text-sm mt-1">Create a group to get started</p>
                </div>
              ) : (
                <div className="divide-y">
                  {groups.map((group) => (
                    <button
                      key={group.id}
                      onClick={() => fetchGroupDetails(group.id)}
                      className={`w-full p-4 text-left hover:bg-slate-50 flex items-center gap-3 transition-colors ${
                        selectedGroup?.id === group.id ? "bg-blue-50" : ""
                      }`}
                      data-testid={`group-${group.id}`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        selectedGroup?.id === group.id ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"
                      }`}>
                        <Users className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-slate-800 truncate">{group.name}</h3>
                        <p className="text-sm text-slate-500">{group.member_count} companies</p>
                      </div>
                      <ChevronRight className={`w-5 h-5 ${
                        selectedGroup?.id === group.id ? "text-blue-600" : "text-slate-400"
                      }`} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Group Details */}
          <div className="lg:col-span-2">
            {selectedGroup ? (
              <div className="bg-white rounded-xl border shadow-sm">
                {/* Group Header */}
                <div className="p-6 border-b">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-xl font-bold text-slate-800">{selectedGroup.name}</h2>
                      {selectedGroup.description && (
                        <p className="text-slate-500 mt-1">{selectedGroup.description}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(selectedGroup)}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => handleDeleteGroup(selectedGroup.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Members Section */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-slate-800">
                      Member Companies ({selectedGroup.member_companies?.length || 0})
                    </h3>
                    <Button
                      size="sm"
                      onClick={() => setShowAddMemberDialog(true)}
                      data-testid="add-member-btn"
                    >
                      <UserPlus className="w-4 h-4 mr-1" />
                      Add Company
                    </Button>
                  </div>

                  {!selectedGroup.member_companies?.length ? (
                    <div className="text-center py-8 text-slate-400 border-2 border-dashed rounded-xl">
                      <Building2 className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p>No companies in this group yet</p>
                      <Button
                        variant="link"
                        onClick={() => setShowAddMemberDialog(true)}
                        className="mt-2"
                      >
                        Add your first company
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {selectedGroup.member_companies.map((member) => (
                        <div
                          key={member.company_id}
                          className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg"
                        >
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-slate-800 truncate">
                              {member.company_name}
                            </h4>
                            <p className="text-xs text-slate-500">
                              Added {new Date(member.added_at).toLocaleDateString()}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => handleRemoveMember(member.company_id, member.company_name)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Info Box */}
                <div className="px-6 pb-6">
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                    <h4 className="font-medium text-blue-800 mb-1">How Groups Work</h4>
                    <p className="text-sm text-blue-700">
                      When a customer from any member company logs in, they will see 
                      shipments from <strong>all</strong> companies in this group combined. 
                      Finances remain tracked separately per company.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl border shadow-sm p-12 text-center text-slate-400">
                <Users className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <h3 className="text-lg font-medium text-slate-600 mb-2">Select a Group</h3>
                <p>Choose a group from the list to view and manage its members</p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Create/Edit Group Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingGroup ? "Edit Group" : "Create Customer Group"}</DialogTitle>
            <DialogDescription>
              Group related companies to give customers a unified view of all their shipments.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Group Name *
              </label>
              <Input
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="e.g., Pactra Group"
                data-testid="group-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Description
              </label>
              <Textarea
                value={groupDescription}
                onChange={(e) => setGroupDescription(e.target.value)}
                placeholder="Optional description..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateDialog(false);
              setEditingGroup(null);
              setGroupName("");
              setGroupDescription("");
            }}>
              Cancel
            </Button>
            <Button onClick={handleCreateGroup} disabled={saving} data-testid="save-group-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingGroup ? "Update" : "Create"} Group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={showAddMemberDialog} onOpenChange={setShowAddMemberDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Company to Group</DialogTitle>
            <DialogDescription>
              Search and select a company to add to "{selectedGroup?.name}"
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={companySearch}
                onChange={(e) => setCompanySearch(e.target.value)}
                placeholder="Search companies..."
                className="pl-10"
                data-testid="company-search-input"
              />
              {searching && (
                <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />
              )}
            </div>

            {searchResults.length > 0 && (
              <div className="mt-3 max-h-64 overflow-y-auto border rounded-lg divide-y">
                {searchResults.map((company) => {
                  const isAlreadyMember = selectedGroup?.member_companies?.some(
                    m => m.company_id === company.id
                  );
                  
                  return (
                    <button
                      key={company.id}
                      onClick={() => !isAlreadyMember && handleAddMember(company)}
                      disabled={isAlreadyMember || addingMember}
                      className={`w-full p-3 text-left flex items-center gap-3 ${
                        isAlreadyMember 
                          ? "bg-green-50 cursor-default" 
                          : "hover:bg-slate-50"
                      }`}
                    >
                      <Building2 className={`w-5 h-5 ${isAlreadyMember ? "text-green-600" : "text-slate-400"}`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-800 truncate">{company.name}</p>
                        <p className="text-xs text-slate-500">
                          {company.city}, {company.country}
                        </p>
                      </div>
                      {isAlreadyMember && (
                        <span className="text-xs text-green-600 flex items-center gap-1">
                          <Check className="w-3 h-3" /> Added
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {companySearch.length >= 2 && searchResults.length === 0 && !searching && (
              <p className="text-center text-slate-400 py-4">No companies found</p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowAddMemberDialog(false);
              setCompanySearch("");
              setSearchResults([]);
            }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
